import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { defaultAuthToastContainerProps } from "../../config/reactToastifyDefaults";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";

import { Button, Input } from "../../components/ui";
import AuthHeroPanel from "../../components/auth/AuthHeroPanel";
import { signin, signup } from "../../utils";
import { applySigninSession } from "../../utils/authSession";
import { registerSchema } from "../../validators/authValidators";
import { createCheckoutSession, getActivatedTrialMessage } from "../../utils/checkout";
import { getTrialCopyFromLaunchFlag } from "../../utils/trialCopy";

type PlanType = "growth" | "professional";
type IntervalType = "monthly" | "quarterly" | "yearly";

/** Register CTA — user already picked a plan on the website (or lands here directly). */
const REGISTER_CTA_LABEL = "Start 7-Day Free Trial";

export default function AuthRegisterPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  // Paid billing (Safepay/Stripe): 7-day trial copy. Early-joiner launch uses 1.5 months.
  const trialCopy = getTrialCopyFromLaunchFlag(false);
  const registerCtaLabel =
    trialCopy.ctaLabel === "Choose a plan" ? REGISTER_CTA_LABEL : trialCopy.ctaLabel;

  const completeAutoSignIn = async (email: string, password: string) => {
    const signinResult = await signin(email, password);
    if (applySigninSession(signinResult, dispatch, email)) {
      toast.success("Welcome to Innlead!");
      setTimeout(() => navigate("/", { replace: true }), 300);
      return true;
    }
    toast.info("Account created. Please sign in.");
    setTimeout(() => navigate("/login"), 500);
    return false;
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      password: "",
    },
    validationSchema: registerSchema,
    onSubmit: async (values) => {
      try {
        const result = await signup(values.email, values.password, {
          firstName: values.firstName,
          lastName: values.lastName,
          company: values.company,
        });
        if (result?.success) {
          toast.success("Account created successfully");
          const wantsTrialCheckout = searchParams.get("trial") === "1";
          const intervalParam = (searchParams.get("interval") || "monthly").toLowerCase();
          const planParam = (searchParams.get("plan") || "growth").toLowerCase();
          const interval: IntervalType =
            intervalParam === "quarterly" || intervalParam === "yearly"
              ? (intervalParam as IntervalType)
              : "monthly";
          const plan: PlanType = planParam === "professional" ? "professional" : "growth";

          if (wantsTrialCheckout && (result?.ownerId || result?.userId)) {
            try {
              const checkout = await createCheckoutSession({
                ownerId: result.ownerId,
                userId: result.userId,
                plan,
                interval,
                trial: true,
              });
              if (checkout.mode === "activated") {
                toast.success(getActivatedTrialMessage(checkout));
                await completeAutoSignIn(values.email, values.password);
                return;
              }
              if (checkout.mode === "checkout") {
                setTimeout(() => {
                  window.location.href = checkout.url;
                }, 250);
                return;
              }
            } catch (checkoutError: any) {
              toast.error(checkoutError?.message || "Unable to activate trial");
              await completeAutoSignIn(values.email, values.password);
              return;
            }
          }
          await completeAutoSignIn(values.email, values.password);
        } else {
          toast.error(result?.error || result?.msg || "Registration failed");
        }
      } catch (error) {
        toast.error("Registration failed");
      }
    },
  });

  return (
    <section className="lg:flex min-h-screen">
      <div className="bg-white w-full lg:w-[38%] flex items-center justify-center px-5 sm:px-8 xl:px-12 py-8">
        <div className="w-full">
          <button
            type="button"
            onClick={() => {
              const url = process.env.REACT_APP_WEBSITE_URL || "/";
              window.location.href = url;
            }}
            className="inline-flex"
            aria-label="Go to website"
          >
            <img src="/new-logo/logo.png" alt="Innlead" className="h-12 w-auto cursor-pointer" />
          </button>

          <h2 className="mt-8 text-h4 font-semibold text-black">Register</h2>
          <p className="mt-2 text-sm text-black/60">
            Already have an account?{" "}
            <Link to="/login" className="text-brand-blue font-medium underline">
              Sign in
            </Link>
          </p>

          <form onSubmit={formik.handleSubmit} className="mt-10 flex flex-col gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="First name"
                id="register-first-name"
                placeholder="First"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName ? formik.errors.firstName : ""}
              />
              <Input
                label="Last name"
                id="register-last-name"
                placeholder="Last"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName ? formik.errors.lastName : ""}
              />
            </div>

            <Input
              label="Company"
              id="register-company"
              placeholder="Company name"
              name="company"
              value={formik.values.company}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.company ? formik.errors.company : ""}
            />

            <Input
              label="Your Email address"
              id="register-email"
              placeholder="Email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email ? formik.errors.email : ""}
            />

            <Input
              label="Create a password"
              id="register-password"
              type="password"
              placeholder="Password"
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password ? formik.errors.password : ""}
            />

            <p className="text-[10px] text-black/60 leading-[14px]">
              By clicking “{registerCtaLabel}” below, you acknowledge that you have read and understood, and agree to
              Innlead <span className="text-brand-blue underline">Terms &amp; Conditions</span> and{" "}
              <span className="text-brand-blue underline">Privacy Policy</span>.
            </p>

            <Button variant="primary" fullWidth size="lg" type="submit" className="!text-black">
              {registerCtaLabel}
            </Button>
          </form>

          <ToastContainer {...defaultAuthToastContainerProps} />
        </div>
      </div>

      <AuthHeroPanel />
    </section>
  );
}
